import type { ReactNode } from "react";
import { PeriodControl, Segmented } from "../components/Controls";
import { EditableAppName } from "../components/EditableAppName";
import { RatingCard, RpiKpi } from "../components/insights";
import { AppIcon, Card, Chip, CommandCenterButton, EmptyState, KpiCard } from "../components/ui";
import { PERIOD_LONG, eur, int, kindMeta, linkStatusMeta, relativeNl } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import { Link } from "../lib/router";
import type { AdminAppDetail } from "../lib/api-admin";
import type { AppMetricsResponse } from "@apphq/shared/types";

export type MobileTab = "omzet" | "gebruik";
const MOBILE_TABS: ReadonlyArray<{ value: MobileTab; label: string }> = [
  { value: "omzet", label: "Omzet" },
  { value: "gebruik", label: "Gebruik" },
];

/** SCREENS §7 "MobielApp": back button, big icon + status, segmented Omzet | Gebruik, 2x2 tiles, recent list, floating command-center bar. */
export function AppDetailMobile({
  app,
  onRenamed,
  metrics,
  tab,
  onTab,
  usage,
  loading,
}: {
  app: AdminAppDetail["app"];
  /** Re-fetch the app after an inline rename. */
  onRenamed: () => void;
  metrics: AppMetricsResponse | null;
  tab: MobileTab;
  onTab: (t: MobileTab) => void;
  /** The shared device cards, rendered by AppDetail so desktop and phone use the same component. */
  usage: ReactNode;
  /** Children for the revenue tab when metrics are not there yet (loading / error). */
  loading: ReactNode;
}) {
  const { period } = usePrefs();
  const link = metrics ? linkStatusMeta(metrics.link_status) : null;
  return (
    <div className="m-app">
      <Link to="/" className="m-back glass" aria-label="Terug naar Overzicht">
        ‹ Overzicht
      </Link>

      <div className="m-app-hero">
        <AppIcon name={app.display_name ?? app.name} url={app.icon_url} size={72} seed={app.id} />
        <div className="m-app-hero-text">
          <h1 className="m-title m-title-app"><EditableAppName app={app} onSaved={onRenamed} /></h1>
          <div className="chips">
            {link && <Chip tone={link.tone}>{link.label}</Chip>}
            {app.status !== "active" && <Chip tone="warn">{app.status === "pending" ? "In afwachting" : "Gearchiveerd"}</Chip>}
          </div>
          <div className="muted small mono m-bundle">{app.bundle_id ?? "Geen bundle-ID"}</div>
        </div>
      </div>

      <div className="m-glass-bar glass">
        <Segmented<MobileTab> label="Onderdeel" value={tab} options={MOBILE_TABS} onChange={onTab} />
      </div>

      {tab === "omzet" && (
        <>
          <div className="m-glass-bar glass">
            <PeriodControl />
          </div>
          {!metrics ? (
            loading
          ) : (
            <>
              <div className="m-kpi-grid">
                <KpiCard label="MRR" value={eur(metrics.kpis.mrr)} />
                <KpiCard label="Omzet" value={eur(metrics.kpis.revenue)} hint={PERIOD_LONG[period]} />
                <KpiCard label="Op telefoon" value={int(metrics.kpis.active_on_phone.value)} previous={{ current: metrics.kpis.active_on_phone.value, before: metrics.kpis.active_on_phone.previous }} />
                <KpiCard label="Abonnees" value={int(metrics.kpis.active_subscriptions)} />
              </div>
              <div className="m-kpi-grid">
                <RpiKpi rpi={metrics.kpis.rpi} />
              </div>
              <RatingCard ratings={metrics.ratings} />
              <Card title="Recent">
                {metrics.recent_transactions.length === 0 ? (
                  <EmptyState title="Geen transacties in deze periode" />
                ) : (
                  <ul className="m-recent">
                    {metrics.recent_transactions.slice(0, 6).map((t) => {
                      const kind = kindMeta(t.kind);
                      return (
                        <li key={t.id}>
                          <span className="m-recent-main">
                            <Chip tone={kind.tone}>{kind.label}</Chip>
                            <span className="muted small mono">{t.product_id}</span>
                          </span>
                          <span className="m-recent-side">
                            <strong>{eur(t.amount_eur)}</strong>
                            <span className="muted small">{relativeNl(t.occurred_at)}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            </>
          )}
        </>
      )}
      {tab === "gebruik" && usage}

      <div className="m-cc-bar glass">
        <span className="muted">Command center</span>
        <CommandCenterButton url={app.command_center_url} appId={app.id} showUnset />
      </div>
    </div>
  );
}
